import React, { useCallback, useEffect, useState } from 'react';
import { MediaPreviewState } from 'screens/EpisodeOptionsScreen/components/EpisodeContent/MediaPreviewState';
import { useFetchEpisodeOverview } from 'client/hooks/episodes/useFetchEpisodeOverview';
import { useSaveAudioToEpisode } from 'client/hooks/episodes/useSaveAudioToEpisode';
import { useSaveVideoToEpisode } from 'client/hooks/episodes/useSaveVideoToEpisode';
import { useUploadFileMultipart } from 'client/hooks/upload/useUploadFileMultipart';
import { useDeleteMediaFromEpisode } from 'client/hooks/episodes/useDeleteMediaFromEpisode';
import { useProcessFile } from 'client/hooks/upload/useProcessFile';
import { useQuickUploadFile } from 'contexts/QuickUploadFile';
import { EpisodeAudios } from 'modules/AnchorAPI/v3/episodes/fetchEpisodeOverview';
import { UploadingState } from './UploadingState';
import { EpisodeContentLabel, UploaderStateContainer } from './styles';
import { IdleState } from './IdleState';
import { DeleteMediaConfirmationModal } from './DeleteMediaConfirmationModal';
import { getMediaPlayerTitle } from './utils';

type PlayerInfo = {
  episodeAudios: EpisodeAudios[];
  containsMedia: boolean;
  title: string;
  totalDuration: number;
  totalSize: number;
  isMultiSegmentFile: boolean;
  isVideoEpisode: boolean;
};

export const EpisodeContent = ({ webEpisodeId }: { webEpisodeId: string }) => {
  const { episodeOverviewData } = useFetchEpisodeOverview({ webEpisodeId });
  const { saveAudioToEpisode } = useSaveAudioToEpisode();
  const { saveVideoToEpisode } = useSaveVideoToEpisode();
  const {
    deleteMediaFromEpisode,
    deleteMediaFromEpisodeStatus,
  } = useDeleteMediaFromEpisode();
  const { quickUploadFile } = useQuickUploadFile();
  const [isDragging, setDragging] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const [playerInfo, setPlayerInfo] = useState<PlayerInfo>({
    episodeAudios: [],
    containsMedia: false,
    title: '',
    totalDuration: 0,
    totalSize: 0,
    isMultiSegmentFile: false,
    isVideoEpisode: false,
  });
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [
    showDeleteConfirmationModal,
    setShowDeleteConfirmationModal,
  ] = useState(false);

  useEffect(() => {
    if (episodeOverviewData) {
      const {
        episodeAudios,
        title,
        totalSize,
        totalDuration,
        videoPodcastingEpisode,
      } = episodeOverviewData;
      setPlayerInfo({
        episodeAudios: episodeAudios || [],
        containsMedia: episodeAudios.length > 0,
        title: getMediaPlayerTitle(episodeAudios, title),
        totalDuration,
        totalSize,
        isMultiSegmentFile: episodeAudios.length > 1,
        isVideoEpisode: videoPodcastingEpisode,
      });
    }
  }, [episodeOverviewData]);

  // TODO : Need generic error state from design to show when an
  // error occus on upload, start of processing, or polling processing
  const onError = () => console.log('Error');

  const { uploadFileMultipart } = useUploadFileMultipart();
  const { processFile } = useProcessFile({
    onProcessingSuccess: processedFile => {
      setProcessing(false);
      const isAudio = processedFile?.audioId && !processedFile?.videoUploadId;
      const isVideo = processedFile?.videoUploadId;

      if (isAudio) {
        saveAudioToEpisode(
          { webEpisodeId, audioId: processedFile?.audioId! },
          {
            onError,
          }
        );
      }

      if (isVideo) {
        saveVideoToEpisode(
          {
            webEpisodeId,
            videoUploadId: processedFile?.videoUploadId!,
          },
          {
            onError,
          }
        );
      }
    },
    onError,
  });

  const onProgressUpload = (loaded: number, total: number) => {
    const percent = Math.round(Math.round((loaded / total) * 100 * 100) / 100);
    setUploadPercentage(percent);
  };

  const onUploadFile = useCallback(
    (file: File) => {
      setUploading(true);
      uploadFileMultipart(
        {
          file,
          onProgress: (_, loaded: number, total: number) =>
            onProgressUpload(loaded, total),
          onError,
        },
        {
          onSuccess: res => {
            setUploading(false);
            setProcessing(true);
            processFile({ file, webEpisodeId, ...res });
          },
        }
      );
    },
    [uploadFileMultipart, processFile, webEpisodeId]
  );

  // Upload quickUploadFile from QuickUploadFile
  // context if present
  useEffect(() => {
    if (quickUploadFile) {
      onUploadFile(quickUploadFile);
    }
  }, [onUploadFile, quickUploadFile]);

  const onCancelUpload = () => {
    // TODO : Will be implemented by WHEEL-789
    // https://anchorfm.atlassian.net/browse/WHEEL-789
    setUploading(false);
  };

  const onDragEnter = () => setDragging(true);
  const onDragLeave = () => setDragging(false);

  const onDeleteUpload = () => {
    deleteMediaFromEpisode(
      { webEpisodeId },
      {
        onSuccess: () => setShowDeleteConfirmationModal(false),
        onError,
      }
    );
  };

  const renderContent = () => {
    if (isUploading) {
      return (
        <UploadingState
          percentageComplete={uploadPercentage}
          onCancel={onCancelUpload}
        />
      );
    }
    if (isProcessing) {
      return (
        <UploadingState onCancel={onCancelUpload} isProcessing={isProcessing} />
      );
    }
    if (playerInfo.containsMedia) {
      return (
        <MediaPreviewState
          episodeAudios={playerInfo.episodeAudios}
          isVideo={playerInfo.isVideoEpisode}
          title={playerInfo.title}
          totalSize={playerInfo.totalSize}
          onClickDelete={() => setShowDeleteConfirmationModal(true)}
          webEpisodeId={webEpisodeId}
        />
      );
    }
    return (
      <IdleState
        onUploadFile={onUploadFile}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
      />
    );
  };

  return (
    <div>
      <EpisodeContentLabel>Episode content</EpisodeContentLabel>
      {showDeleteConfirmationModal && (
        <DeleteMediaConfirmationModal
          onDelete={onDeleteUpload}
          deleteStatus={deleteMediaFromEpisodeStatus}
          hideConfirmationModal={() => setShowDeleteConfirmationModal(false)}
        />
      )}
      <UploaderStateContainer
        isExpanded={
          (!playerInfo.containsMedia && !isUploading && !isProcessing) ||
          (playerInfo.containsMedia && playerInfo.isVideoEpisode)
        }
        containsMedia={playerInfo.containsMedia}
        isDragging={isDragging}
      >
        {renderContent()}
      </UploaderStateContainer>
    </div>
  );
};
